/**
 * API e hook della dashboard.
 *
 * ⚠️ Tutti i campi monetari sono STRINGHE decimali (ADR-0006). Non convertirli
 * in `number` per fare calcoli: si formattano con `lib/format.ts`.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
export { usePortfolios } from '@/features/portfolios/api';
export type { PortfolioSummary } from '@/features/portfolios/api';

export type CurrencyTotal = {
  currency: string;
  /** Solo i titoli dell'ultimo snapshot. */
  totalValue: string;
  /** Liquidità dei portafogli reali: somma dei movimenti. */
  cashBalance: string;
  /** Titoli più liquidità, sommati dal server: il patrimonio vero. */
  totalWithCash: string;
  portfolioCount: number;
};

export type AllocationSlice = {
  key: string;
  label: string;
  currency: string;
  /** Solo i titoli. */
  totalValue: string;
  /** Liquidità dei portafogli della fetta: la seconda tonalità della barra. */
  cashBalance: string;
  totalWithCash: string;
};

export type TopHolding = {
  instrumentId: string;
  name: string;
  symbol: string | null;
  isin: string | null;
  instrumentType: string;
  portfolioName: string;
  quantity: string;
  marketValue: string | null;
  unrealizedPl: string | null;
  unrealizedPlPct: string | null;
  currency: string;
};

export type CashFlow = {
  currency: string;
  deposits: string;
  withdrawals: string;
  dividends: string;
  interest: string;
  fees: string;
  taxes: string;
};

export type RecentTransaction = {
  id: string;
  txType: string;
  txDate: string;
  amount: string;
  quantity: string | null;
  currency: string;
  portfolioName: string;
  instrumentName: string | null;
};

export type DashboardSummary = {
  baseCurrency: string;
  counts: {
    portfolios: number;
    activePortfolios: number;
    /** Portafogli simulati: contati, ma esclusi da ogni aggregato economico. */
    simulatedPortfolios: number;
    /** Collegati a un conto DEMO del broker: esclusi dai totali come i simulati. */
    demoPortfolios: number;
    instruments: number;
    transactions: number;
    imports: number;
  };
  lastSnapshotDate: string | null;
  totalsByCurrency: CurrencyTotal[];
  allocationByType: AllocationSlice[];
  allocationByPlatform: AllocationSlice[];
  topHoldings: TopHolding[];
  cashFlow: CashFlow[];
  recentTransactions: RecentTransaction[];
  hasMixedCurrencies: boolean;
};

export const dashboardKeys = {
  summary: ['dashboard', 'summary'] as const,
};

export function useDashboardSummary() {
  return useQuery({
    queryKey: dashboardKeys.summary,
    queryFn: ({ signal }) => api.get<DashboardSummary>('/dashboard/summary', signal),
  });
}
